import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PhoneOutgoing, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const OutboundCallCard = () => {
  const [options, setOptions] = useState(null);
  const [error, setError] = useState("");
  const [phoneId, setPhoneId] = useState("");
  const [assistantId, setAssistantId] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/outbound-call/options");
      setOptions(data);
      setPhoneId(data.selected.phone_number_id || "");
      setAssistantId(data.selected.assistant_id || "");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not load Vapi numbers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/outbound-call", { phone_number_id: phoneId, assistant_id: assistantId });
      setOptions((o) => ({ ...o, selected: data }));
      toast.success(`Outbound calls will dial from ${data.phone_number} using ${data.assistant_name}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const dirty = phoneId !== (options?.selected.phone_number_id || "") || assistantId !== (options?.selected.assistant_id || "");

  return (
    <div className="border border-border bg-[#0A0A0A] rounded-sm p-6 space-y-4" data-testid="outbound-call-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <PhoneOutgoing className="w-4 h-4 text-[#34C759]" />
          <h2 className="font-heading text-lg font-black tracking-tight">Outbound calls</h2>
        </div>
        <button type="button" onClick={load} disabled={loading} data-testid="outbound-refresh-btn"
          className="text-muted-foreground hover:text-white transition-colors duration-200" title="Reload from Vapi">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>
      <p className="text-xs text-muted-foreground">
        Pick the Vapi phone number the inquiry representative dials from and the assistant whose voice it speaks with.
        The five spoken questions are generated per call; only the voice and model settings are taken from the assistant.
      </p>
      {error ? (
        <p className="text-xs text-[#FF3B30]" data-testid="outbound-error">{error}</p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Calling number</Label>
            <Select value={phoneId} onValueChange={setPhoneId} disabled={loading}>
              <SelectTrigger data-testid="outbound-phone-select" className="mt-2 rounded-sm bg-background border-border">
                <SelectValue placeholder={loading ? "Loading…" : "Choose a number"} />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-border rounded-sm">
                {(options?.phone_numbers || []).map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.number}{p.name ? ` — ${p.name}` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Voice (assistant)</Label>
            <Select value={assistantId} onValueChange={setAssistantId} disabled={loading}>
              <SelectTrigger data-testid="outbound-assistant-select" className="mt-2 rounded-sm bg-background border-border">
                <SelectValue placeholder={loading ? "Loading…" : "Choose an assistant"} />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-border rounded-sm">
                {(options?.assistants || []).map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.name}{a.voice ? ` — ${a.voice}` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground font-mono" data-testid="outbound-selected-label">
          {options?.selected.phone_number_id
            ? `Active: ${options.selected.phone_number} · ${options.selected.assistant_name}`
            : "Not configured — users can't place calls yet."}
        </p>
        <Button size="sm" onClick={save} disabled={!phoneId || !assistantId || !dirty || saving} data-testid="outbound-save-btn"
          className="rounded-sm bg-[#007AFF] hover:bg-[#0066d6] text-white font-semibold text-xs gap-2">
          {saving && <Loader2 className="w-3 h-3 animate-spin" />} Save
        </Button>
      </div>
    </div>
  );
};
